import React, { useState, useRef, useEffect } from 'react';
import {
  Pane,
  Textarea,
  Button,
  Spinner,
  toaster,
  IconButton,
  Dialog,
  FileUploader,
  Text,
  Badge,
  CrossIcon,
  DocumentIcon
} from 'evergreen-ui';
import MDEditor from '@uiw/react-md-editor';
import api from '../../services/api';
import { useProject } from '../../context/ProjectContext';

const CommentEditor = ({ 
  onSave, 
  onCancel, 
  initialContent = '', 
  isEditing = false,
  isLoading = false 
}) => {
  const { currentProject } = useProject();
  const [content, setContent] = useState(initialContent);
  const [attachments, setAttachments] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  const editorRef = useRef(null);

  const handleSave = async () => {
    if (!content.trim()) {
      toaster.danger('Comment cannot be empty');
      return;
    }

    try {
      await onSave(content, attachments);
      setContent('');
      setAttachments([]);
    } catch (error) {
      toaster.danger('Failed to save comment');
    }
  };

  const handleFileUpload = async (files) => {
    if (files.length === 0) return;

    setUploadingFiles(true);
    const newAttachments = [...attachments];

    try {
      for (const file of files) {
        // Validate file size (10MB limit)
        if (file.size > 10 * 1024 * 1024) {
          toaster.danger(`File ${file.name} is too large (max 10MB)`);
          continue;
        }

        // Create preview object for file
        const preview = {
          id: Date.now() + Math.random(),
          file,
          name: file.name,
          size: file.size,
          type: file.type,
          uploading: false,
          serverData: null // We'll handle this in the comment creation
        };
        newAttachments.push(preview);
        setAttachments([...newAttachments]);
      }
    } finally {
      setUploadingFiles(false);
    }
  };

  // Find textarea inside MDEditor
  const findTextarea = () => {
    if (!editorRef.current) return null;
    const textarea = editorRef.current.querySelector('textarea');
    return textarea;
  };

  const handlePaste = async (event) => {
    const items = event.clipboardData?.items;
    if (!items || !currentProject?.id) return;

    const imageFiles = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type && item.type.indexOf('image') !== -1) {
        const file = item.getAsFile();
        if (file) {
          imageFiles.push(file);
        }
      }
    }

    if (imageFiles.length > 0) {
      event.preventDefault();
      event.stopPropagation();
      setUploadingImage(true);
      
      // Get textarea and cursor position
      const textarea = findTextarea() || event.target;
      let cursorStart = content.length;
      let cursorEnd = content.length;
      
      if (textarea && (textarea.tagName === 'TEXTAREA' || textarea.tagName === 'INPUT')) {
        cursorStart = textarea.selectionStart || content.length;
        cursorEnd = textarea.selectionEnd || content.length;
      }
      
      try {
        for (const file of imageFiles) {
          // Validate file size (10MB limit)
          if (file.size > 10 * 1024 * 1024) {
            toaster.danger(`Image ${file.name || 'image'} is too large (max 10MB)`);
            continue;
          }

          // Upload image to server
          const formData = new FormData();
          formData.append('file', file);
          
          const response = await api.post(
            `/projects/${currentProject.id}/markdown-images`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            }
          );

          // Insert markdown image syntax at cursor position
          const imageUrl = response.data.url;
          const fileName = file.name || 'image';
          const imageMarkdown = `![${fileName}](${imageUrl})\n`;
          
          // Insert at cursor position
          setContent(prev => {
            const before = prev.substring(0, cursorStart);
            const after = prev.substring(cursorEnd);
            const newContent = before + imageMarkdown + after;
            
            // Update cursor position after state update
            setTimeout(() => {
              const updatedTextarea = findTextarea();
              if (updatedTextarea) {
                const newPos = cursorStart + imageMarkdown.length;
                updatedTextarea.setSelectionRange(newPos, newPos);
                updatedTextarea.focus();
              }
            }, 0);
            
            return newContent;
          });
          
          // Update cursor position for next image
          cursorStart += imageMarkdown.length;
          cursorEnd = cursorStart;
          
          toaster.success('Image uploaded and inserted');
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        toaster.danger('Failed to upload image');
      } finally {
        setUploadingImage(false);
      }
    }
  };

  // Add paste event listener to editor container
  useEffect(() => {
    const editorContainer = editorRef.current;
    if (!editorContainer) return;

    const handleContainerPaste = (event) => {
      // Only handle if it's an image paste
      const items = event.clipboardData?.items;
      if (!items || !currentProject?.id) return;

      let hasImage = false;
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type && item.type.indexOf('image') !== -1) {
          hasImage = true;
          break;
    }
      }

      if (hasImage) {
        handlePaste(event);
      }
    };

    editorContainer.addEventListener('paste', handleContainerPaste, true);
    
    return () => {
      editorContainer.removeEventListener('paste', handleContainerPaste, true);
    };
  }, [content, currentProject]);

  const removeAttachment = (attachmentId) => {
    setAttachments(attachments.filter(a => a.id !== attachmentId));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Pane>
      <Pane marginBottom={12} ref={editorRef}>
        <MDEditor
          value={content}
          onChange={setContent}
          height={200}
          preview="edit"
          data-color-mode="light"
          style={{ backgroundColor: 'white' }}
          onPaste={handlePaste}
        />
        {uploadingImage && (
          <Pane marginTop={8} display="flex" alignItems="center" gap={8}>
            <Spinner size={16} />
            <Text size={300} color="muted">Uploading image...</Text>
          </Pane>
        )}
      </Pane>

      {/* File Upload Area */}
      <Pane marginBottom={12}>
        <FileUploader
          ref={fileInputRef}
          accept="image/*,.pdf,.txt,.doc,.docx"
          multiple
          disabled={uploadingFiles}
          onChange={handleFileUpload}
          renderFileUploader={({ getInputProps, getRootProps, openFileDialog }) => (
            <Pane
              {...getRootProps()}
              border="2px dashed #E6E8F0"
              borderRadius={6}
              padding={16}
              textAlign="center"
              cursor="pointer"
              backgroundColor="#F7F8FA"
              hoverBackgroundColor="#EDF0F2"
              onClick={openFileDialog}
            >
              <input {...getInputProps()} />
              <DocumentIcon size={24} color="muted" marginBottom={8} />
              <Text size={400} color="muted">
                {uploadingFiles ? 'Uploading...' : 'Click to upload files or drag and drop'}
              </Text>
              <Text size={300} color="muted" marginTop={4}>
                Images, PDFs, documents (max 10MB each)
              </Text>
            </Pane>
          )}
        />
      </Pane>

      {/* Attachments Preview */}
      {attachments.length > 0 && (
        <Pane marginBottom={12}>
          <Text size={400} fontWeight="bold" marginBottom={8}>
            Attachments ({attachments.length})
          </Text>
          <Pane display="flex" flexWrap="wrap" gap={8}>
            {attachments.map((attachment) => (
              <Pane
                key={attachment.id}
                display="flex"
                flexDirection="column"
                padding={8}
                backgroundColor="tint1"
                borderRadius={4}
                border="1px solid #E6E8F0"
                minWidth={200}
                maxWidth={300}
              >
                {/* Image Preview */}
                {attachment.type && attachment.type.startsWith('image/') && (
                  <Pane marginBottom={8} textAlign="center">
                    <img
                      src={URL.createObjectURL(attachment.file)}
                      alt={attachment.name}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100px',
                        borderRadius: '4px'
                      }}
                    />
                  </Pane>
                )}
                
                {/* File Info */}
                <Pane display="flex" alignItems="center">
                  <DocumentIcon size={16} marginRight={8} />
                  <Pane flex={1}>
                    <Text size={300} fontWeight="bold">
                      {attachment.name}
                    </Text>
                    <Text size={300} color="muted">
                      {formatFileSize(attachment.size)}
                    </Text>
                  </Pane>
                  {attachment.uploading ? (
                    <Spinner size={16} />
                  ) : (
                    <IconButton
                      icon={CrossIcon}
                      size="small"
                      appearance="minimal"
                      onClick={() => removeAttachment(attachment.id)}
                    />
                  )}
                </Pane>
              </Pane>
            ))}
          </Pane>
        </Pane>
      )}

      {/* Action Buttons */}
      <Pane display="flex" justifyContent="flex-end" gap={8}>
        <Button
          appearance="minimal"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          appearance="primary"
          onClick={handleSave}
          isLoading={isLoading}
          disabled={!content.trim() || uploadingFiles}
        >
          {isEditing ? 'Update Comment' : 'Post Comment'}
        </Button>
      </Pane>
    </Pane>
  );
};

export default CommentEditor;
